/**
 * @fileoverview Сопоставление полей mention с данными из сервисов
 * @description Логика поиска и сопоставления mention узлов с полученными данными
 */

/**
 * Создает многоуровневую карту для быстрого поиска полей
 * Map<service_code, Map<key, Map<participant_type_code, field>>>
 *
 * @param {import('./types').ServiceField[]} fields - Массив полей из сервиса
 * @returns {Map<string, Map<string, Map<string, any>>>} Многоуровневая карта
 *
 * @example
 * const fields = [
 *   { key: 'first_name', value: 'Айгуль', service_code: 'gbdfl', participant_type_code: 'supruga' },
 *   { key: 'last_name', value: 'Иванова', service_code: 'gbdfl', participant_type_code: 'supruga' }
 * ];
 * const map = createFieldsMap(fields);
 * // Map { 'gbdfl' => Map { 'first_name' => Map { 'supruga' => {...} } } }
 */
export function createFieldsMap(fields) {
  const map = new Map();

  if (!Array.isArray(fields) || fields.length === 0) {
    return map;
  }

  fields.forEach((field) => {
    if (!field?.key) {
      return;
    }

    // Для кастомных полей (без service_code) используем пустую строку
    const serviceCode =
      field.service_code !== undefined && field.service_code !== null
        ? field.service_code
        : "";
    const key = field.key;
    const participantType =
      field.participant_type_code === null ||
      field.participant_type_code === undefined
        ? ""
        : String(field.participant_type_code);

    // Создаем трехуровневую структуру: service_code -> key -> participant_type_code
    if (!map.has(serviceCode)) {
      map.set(serviceCode, new Map());
    }

    const serviceMap = map.get(serviceCode);

    if (!serviceMap.has(key)) {
      serviceMap.set(key, new Map());
    }

    const keyMap = serviceMap.get(key);

    keyMap.set(participantType, {
      value: field.value,
      field: field,
    });
  });

  return map;
}

/**
 * Ищет значение в карте полей по service_code, key и participant_type_code
 * Поддерживает fallback на пустой participant_type_code
 *
 * @param {Map} fieldsMap - Карта полей
 * @param {string} service_code - Код сервиса
 * @param {string} key - Ключ поля
 * @param {string} participant_type_code - Код участника
 * @returns {any|null} Найденное значение или null
 */
export function findFieldValue(
  fieldsMap,
  service_code,
  key,
  participant_type_code
) {
  if (!fieldsMap || !key) {
    return null;
  }

  // Для кастомных полей (без service_code) используем пустую строку
  const serviceCodeKey = service_code || "";

  const participantType =
    participant_type_code === null || participant_type_code === undefined
      ? ""
      : String(participant_type_code);

  // Проверяем наличие service_code
  if (!fieldsMap.has(serviceCodeKey)) {
    return null;
  }

  const serviceMap = fieldsMap.get(serviceCodeKey);

  // Проверяем наличие key
  if (!serviceMap.has(key)) {
    return null;
  }

  const keyMap = serviceMap.get(key);

  // Пытаемся найти точное совпадение по participant_type_code
  if (keyMap.has(participantType)) {
    return keyMap.get(participantType);
  }

  // Fallback: если не нашли точное совпадение, ищем с пустым participant_type_code
  if (participantType !== "" && keyMap.has("")) {
    return keyMap.get("");
  }

  return null;
}

/**
 * Проверяет, совпадает ли mention узел с полем
 * @param {import('./types').MentionAttributes} mentionAttrs - Атрибуты mention узла
 * @param {import('./types').ServiceField} field - Поле из сервиса
 * @returns {boolean} true, если совпадает
 */
export function doesMentionMatchField(mentionAttrs, field) {
  if (!mentionAttrs || !field) return false;

  // Проверка service_code
  if (mentionAttrs.service_code !== field.service_code) {
    return false;
  }

  // Проверка key
  if (mentionAttrs.key !== field.key) {
    return false;
  }

  // Проверка participant_type_code (с учетом пустых значений)
  const mentionParticipant = mentionAttrs.participant_type_code || "";
  const fieldParticipant = field.participant_type_code || "";

  return mentionParticipant === fieldParticipant;
}

/**
 * Фильтрует поля по participant_type_code
 * @param {import('./types').ServiceField[]} fields - Массив полей
 * @param {string} participant_type_code - Код участника для фильтрации
 * @returns {import('./types').ServiceField[]} Отфильтрованные поля
 */
export function filterFieldsByParticipant(fields, participant_type_code) {
  if (!Array.isArray(fields)) return [];
  if (!participant_type_code) return fields;

  return fields.filter((field) => {
    const fieldParticipant = field.participant_type_code || "";
    return fieldParticipant === participant_type_code;
  });
}

/**
 * Группирует поля по service_code
 * @param {import('./types').ServiceField[]} fields - Массив полей
 * @returns {Object.<string, import('./types').ServiceField[]>} Сгруппированные поля
 */
export function groupFieldsByService(fields) {
  if (!Array.isArray(fields)) return {};

  return fields.reduce((acc, field) => {
    const serviceCode = field.service_code || "unknown";
    if (!acc[serviceCode]) {
      acc[serviceCode] = [];
    }
    acc[serviceCode].push(field);
    return acc;
  }, {});
}
